import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ProductMaterialContext = createContext();
export const useProductMaterials = () =>
  useContext(ProductMaterialContext);

export const ProductMaterialProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [productMaterials, setProductMaterials] = useState([]);
  const [materialloading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const PRODUCT_MATERIAL_ENDPOINT = "/product-material";
  const baseURL = BASE_URL.endsWith("/")
    ? BASE_URL.slice(0, -1)
    : BASE_URL;

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetProductMaterials = (updater) => {
    if (!mountedRef.current) return;
    setProductMaterials(updater);
  };

  /* ---------------- FETCH ALL ---------------- */
  const fetchProductMaterials = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${baseURL}${PRODUCT_MATERIAL_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(payload?.message || "Fetch failed");

      safeSetProductMaterials(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[ProductMaterials] fetch error:", err);
      if (err.message?.includes("Token")) logout();
      safeSetProductMaterials([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const addProductMaterial = useCallback(
    async (data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${PRODUCT_MATERIAL_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || "Create failed");

        await fetchProductMaterials();
        return payload;
      } catch (err) {
        console.error("[ProductMaterials] add error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchProductMaterials]
  );

  /* ---------------- UPDATE ---------------- */
  const updateProductMaterial = useCallback(
    async (id, data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(
          `${baseURL}${PRODUCT_MATERIAL_ENDPOINT}/${id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              ...data,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || "Update failed");

        await fetchProductMaterials();
        return payload;
      } catch (err) {
        console.error("[ProductMaterials] update error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchProductMaterials]
  );

  /* ---------------- DELETE ---------------- */
  const deleteProductMaterial = useCallback(
    async (id) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(
          `${baseURL}${PRODUCT_MATERIAL_ENDPOINT}/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              delete_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || "Delete failed");

        safeSetProductMaterials((prev) =>
          prev.filter((m) => String(m.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[ProductMaterials] delete error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* ---------------- TOGGLE STATUS ---------------- */
  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      const newStatus =
        currentStatus === "Active" ? "Inactive" : "Active";

      try {
        if (!token) throw new Error("Missing auth token");

        // Optimistic UI
        safeSetProductMaterials((prev) =>
          prev.map((m) =>
            String(m.id) === String(id)
              ? { ...m, status: newStatus }
              : m
          )
        );

        const res = await fetch(
          `${baseURL}${PRODUCT_MATERIAL_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || "Status update failed");

        return payload;
      } catch (err) {
        console.error("[ProductMaterials] toggleStatus error:", err);

        // Rollback UI
        safeSetProductMaterials((prev) =>
          prev.map((m) =>
            String(m.id) === String(id)
              ? { ...m, status: currentStatus }
              : m
          )
        );
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchProductMaterials().catch(() => {});
  }, [isReady, token, fetchProductMaterials]);

  return (
    <ProductMaterialContext.Provider
      value={{
        productMaterials,
        materialloading,
        fetchProductMaterials,
        addProductMaterial,
        updateProductMaterial,
        deleteProductMaterial,
        toggleStatus,
      }}
    >
      {children}
    </ProductMaterialContext.Provider>
  );
};
